import { useState, useEffect } from 'react';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Mail, Phone, MapPin, Clock, Send, Loader2 } from 'lucide-react';
import { api, formatApiError } from '@/admin/api';
import { useSiteSettings } from '@/contexts/SiteSettings';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSelectChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      subject: value,
    }));
  };

  const [submitting, setSubmitting] = useState(false);
  const [content, setContent] = useState({ heading: 'Get in touch', intro: '' });
  const { settings } = useSiteSettings();

  useEffect(() => {
    api.get('/api/content/contact').then((r) => setContent(r.data)).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post('/api/contact', {
        name: formData.name,
        email: formData.email,
        subject: formData.subject ? `${formData.subject}${formData.phone ? ` | ${formData.phone}` : ''}` : (formData.phone || ''),
        message: formData.message,
      });
      toast.success('Message sent!', {
        description: 'Thank you for reaching out. We will respond within 24-48 hours.',
      });
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const contactInfo = [
    settings.contact_email && { icon: Mail, title: 'Email', details: settings.contact_email, link: `mailto:${settings.contact_email}` },
    settings.contact_phone && { icon: Phone, title: 'Phone', details: settings.contact_phone, link: `tel:${settings.contact_phone.replace(/\s/g, '')}` },
    settings.address && { icon: MapPin, title: 'Address', details: settings.address, link: null },
    settings.office_hours && { icon: Clock, title: 'Working Hours', details: settings.office_hours, link: null },
  ].filter(Boolean);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary/10 via-background to-accent/10 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center space-x-2 bg-primary/10 text-primary px-4 py-2 rounded-full mb-6">
              <Send size={18} />
              <span className="text-sm font-medium">Get In Touch</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-bold text-foreground mb-6">
              {content.heading || 'Contact Us'}
            </h1>
            <p className="text-lg lg:text-xl text-muted-foreground leading-relaxed">
              {content.intro || "Have questions or need assistance? We're here to help."}
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-12 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {contactInfo.map((info, index) => {
              const Icon = info.icon;
              return (
                <Card key={index} className="text-center hover:shadow-medium transition-elegant">
                  <CardContent className="pt-8 pb-6 space-y-4">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/10 text-primary">
                      <Icon size={28} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground mb-2">{info.title}</h3>
                      {info.link ? (
                        <a
                          href={info.link}
                          className="text-sm text-muted-foreground hover:text-primary transition-colors"
                        >
                          {info.details}
                        </a>
                      ) : (
                        <p className="text-sm text-muted-foreground">{info.details}</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Contact Form & Map */}
      <section className="py-20 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div>
              <div className="mb-8">
                <h2 className="text-3xl lg:text-4xl font-serif font-bold text-foreground mb-4">
                  Send Us a Message
                </h2>
                <p className="text-lg text-muted-foreground">
                  Fill out the form and our team will get back to you within 24-48 hours.
                </p>
              </div>

              <Card className="shadow-elevated">
                <CardContent className="pt-8 pb-8">
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="space-y-2">
                      <Label htmlFor="name">Full Name *</Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        placeholder="Enter your full name"
                      />
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="email">Email Address *</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          required
                          placeholder="your.email@example.com"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="phone">Phone Number</Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleInputChange}
                          placeholder="+234 XXX XXX XXXX"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="subject">Subject *</Label>
                      <Select value={formData.subject} onValueChange={handleSelectChange}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select a subject" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="membership">Membership Inquiry</SelectItem>
                          <SelectItem value="events">Event Information</SelectItem>
                          <SelectItem value="payment">Payment Support</SelectItem>
                          <SelectItem value="partnership">Partnership Opportunity</SelectItem>
                          <SelectItem value="general">General Inquiry</SelectItem>
                          <SelectItem value="feedback">Feedback</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="message">Message *</Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        required
                        placeholder="Type your message here..."
                        rows={6}
                      />
                    </div>

                    <Button type="submit" disabled={submitting} data-testid="contact-submit-btn" className="w-full bg-primary hover:bg-primary-dark text-white py-6 text-lg">
                      {submitting ? <Loader2 size={20} className="mr-2 animate-spin" /> : <Send size={20} className="mr-2" />}
                      Send Message
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>

            {/* Info Section */}
            <div className="space-y-8">
              {/* Office Location */}
              <Card className="shadow-medium">
                <CardHeader>
                  <CardTitle className="font-serif">Our Office</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="aspect-video bg-muted rounded-lg flex items-center justify-center mb-4">
                    <MapPin size={48} className="text-primary" />
                  </div>
                  <p className="text-muted-foreground leading-relaxed">
                    Visit our national secretariat in Makurdi, Benue State. Our team is available during working hours to assist you with any inquiries.
                  </p>
                </CardContent>
              </Card>

              {/* Social Media — managed in admin Site Settings */}

              {/* FAQ */}
              <Card className="shadow-medium">
                <CardHeader>
                  <CardTitle className="font-serif">Frequently Asked Questions</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <h4 className="font-semibold text-foreground mb-1">How do I become a member?</h4>
                    <p className="text-sm text-muted-foreground">Visit our Membership page to register online or contact us for assistance.</p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground mb-1">What are the membership fees?</h4>
                    <p className="text-sm text-muted-foreground">Regular membership is ₦5,000 annually. We also offer Life and Patron memberships.</p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground mb-1">How can I attend events?</h4>
                    <p className="text-sm text-muted-foreground">Check our Events page for upcoming activities and registration information.</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;
